/**
 * svg-raster.ts — turn a live SVG in the page into a canvas.
 *
 * Used by "copy as image" on the dashboard charts. Recharts renders real SVG,
 * so unlike the board (see `board-raster.ts`) there is nothing to re-draw —
 * the picture already exists and only has to be made portable.
 *
 * ## The one thing that makes this hard
 *
 * An SVG loaded through `new Image()` is rendered in an **isolated document**.
 * It cannot see the page's stylesheets, its CSS custom properties, or its
 * fonts. That matters here because the charts are styled almost entirely in
 * terms of variables:
 *
 *     stroke="var(--chart-line)"
 *     stroke="hsl(var(--chart-grid))"
 *     fill="hsl(var(--muted-foreground))"
 *
 * Serialize that and every one of them resolves to nothing in the new document.
 * `stroke` falls back to none and `fill` to black, so the export comes out as a
 * black-on-transparent skeleton with no line in it — and it fails *silently*,
 * which is why this is worth a long comment rather than a short one.
 *
 * The fix is to resolve the styles while the nodes are still in the page:
 * `getComputedStyle` on each ORIGINAL element, written as plain values onto the
 * matching clone. By the time the clone is serialized it carries `rgb(…)`
 * everywhere and needs nothing from the document it came from.
 *
 * ## Fonts
 *
 * The isolated document cannot load the self-hosted Outfit woff2 either, so
 * text renders in whatever the fallback stack gives. That is cosmetic rather
 * than structural: recharts has already positioned every label with explicit
 * x/y, so a substituted face changes glyph shapes and not layout. Embedding the
 * font would mean base64-ing ~100KB per weight into every copy, which is not
 * worth it for axis ticks. `FALLBACK_FONTS` keeps the substitute close.
 */

/** Properties that have to survive into the isolated document. */
const INLINED = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-anchor',
  'dominant-baseline',
  'letter-spacing',
  'visibility',
  'display',
  'color',
  'stop-color',
  'stop-opacity',
] as const

const FALLBACK_FONTS =
  'Outfit, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

export interface SvgRasterOptions {
  /** Pixels per CSS pixel. 2 keeps a chart sharp when pasted into slides. */
  scale?: number
  /** Paint this behind the chart. Omit for a transparent PNG. */
  background?: string
  /** Space around the chart, in CSS pixels, before scaling. */
  padding?: number
}

/**
 * Clone `svg`, inline its computed styles, and draw it into a canvas.
 *
 * Rejects rather than resolving null when the image fails to decode, because
 * the two failure modes a caller can act on differently are "nothing to copy"
 * (null from `findChartSvg`) and "the copy broke" (a rejection here).
 */
export async function svgToCanvas(
  svg: SVGSVGElement,
  opts: SvgRasterOptions = {},
): Promise<HTMLCanvasElement> {
  const scale = opts.scale ?? 2
  const padding = opts.padding ?? 8

  // The rendered size, not the attributes. A ResponsiveContainer chart carries
  // width/height attributes that match, but a chart inside a flex row may be
  // laid out smaller than it claims, and getBoundingClientRect is the truth.
  const rect = svg.getBoundingClientRect()
  const width = Math.max(1, Math.round(rect.width || svg.clientWidth))
  const height = Math.max(1, Math.round(rect.height || svg.clientHeight))

  const clone = svg.cloneNode(true) as SVGSVGElement
  inlineStyles(svg, clone)

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink')
  clone.setAttribute('width', String(width))
  clone.setAttribute('height', String(height))
  if (!clone.getAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`)
  }
  clone.style.fontFamily = FALLBACK_FONTS

  const svgText = new XMLSerializer().serializeToString(clone)
  // A data: URL rather than a blob: URL. Safari refuses to draw an SVG served
  // from a blob URL into a canvas, reporting success and producing nothing.
  // encodeURIComponent keeps the `#` in every colour from truncating the URL.
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`

  const img = await loadImage(url)

  const canvas = document.createElement('canvas')
  canvas.width = Math.round((width + padding * 2) * scale)
  canvas.height = Math.round((height + padding * 2) * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d canvas context unavailable')

  if (opts.background) {
    ctx.fillStyle = opts.background
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.setTransform(scale, 0, 0, scale, 0, 0)
  ctx.drawImage(img, padding, padding, width, height)
  return canvas
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('the chart could not be rendered as an image'))
    img.src = url
  })
}

/**
 * Walk `source` and `clone` in lockstep, copying resolved styles across.
 *
 * The two trees are identical by construction — `clone` came from
 * `cloneNode(true)` — so index-wise recursion is safe and avoids giving every
 * node a temporary id.
 */
function inlineStyles(source: Element, clone: Element) {
  const cs = getComputedStyle(source)
  let css = ''
  for (const prop of INLINED) {
    const value = cs.getPropertyValue(prop)
    // An unresolved `var(...)` comes back empty, which is exactly the case this
    // function exists for — skipping it leaves the attribute's own value in
    // place, which is the broken `var(--chart-line)` we are replacing. There is
    // nothing better to substitute, so it is left to the browser's default.
    if (value && value !== 'none' && value !== 'normal') {
      css += `${prop}:${value};`
    }
  }
  if (css) clone.setAttribute('style', css)

  const kids = source.children
  const cloneKids = clone.children
  for (let i = 0; i < kids.length; i++) {
    const kid = cloneKids[i]
    if (kid) inlineStyles(kids[i], kid)
  }
}

/**
 * The SVG inside a chart container.
 *
 * Recharts wraps its SVG in a `.recharts-wrapper` div, so a ref on the card
 * finds the chart without the caller knowing recharts' DOM. Falls back to the
 * first SVG in the subtree, which is what the hand-rolled charts
 * (`radar-chart.tsx`, `category-trend.tsx`, `stack-timeline.tsx`) need.
 */
export function findChartSvg(root: HTMLElement | null): SVGSVGElement | null {
  if (!root) return null
  return (
    root.querySelector<SVGSVGElement>('.recharts-wrapper svg') ??
    root.querySelector<SVGSVGElement>('svg')
  )
}
